'use client';

import {
    BookOpen,
    Filter,
    FolderOpen,
    Loader2,
    RefreshCw,
    Search,
    X,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChapterTable } from '../../Components/studio/ChapterTable';
import { Button } from '../../Components/ui/button';
import { Card, CardContent } from '../../Components/ui/card';
import { Input } from '../../Components/ui/input';
import { Label } from '../../Components/ui/label';
import { PrismFluxLoader } from '../../Components/ui/prism-flux-loader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../Components/ui/select';
import { chapterHasContentForLanguage } from '../../lib/firebase/utils/languageAvailability';
import {
    getChapters,
    GetChaptersResult,
    getClasses,
    getCurriculums,
    getSubjects,
} from '../../lib/firestore/queries';
import {
    Chapter,
    Class,
    ContentFilters,
    Curriculum,
    LanguageCode,
    Subject,
} from '../../types/curriculum';
import type { CurriculumChapter } from '../../types/firebase';

const ContentLibrary = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  
  // Filter options
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  
  // Selected filters
  const [filters, setFilters] = useState<ContentFilters>({
    curriculum: '',
    classId: '',
    subject: '',
    search: '',
  });
  
  // Data state
  const [rawChapters, setRawChapters] = useState<Array<{ id: string; data: CurriculumChapter }>>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<unknown>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('en');
  
  // Load curriculums on mount
  useEffect(() => {
    const loadCurriculums = async () => {
      try {
        const data = await getCurriculums();
        setCurriculums(data);
        if (data.length > 0) {
          setFilters((f) => ({ ...f, curriculum: data[0].id }));
        }
      } catch (error) {
        console.error('Error loading curriculums:', error);
        toast.error('Failed to load curriculums');
      } finally {
        setLoadingFilters(false);
      }
    };
    loadCurriculums();
  }, []);
  
  // Load classes when curriculum changes
  useEffect(() => {
    if (!filters.curriculum) {
      setClasses([]);
      return;
    }
    
    const loadClasses = async () => {
      try {
        const data = await getClasses(filters.curriculum);
        setClasses(data);
        if (data.length > 0) {
          setFilters((f) => ({ ...f, classId: data[0].id }));
        } else {
          setFilters((f) => ({ ...f, classId: '', subject: '' }));
        }
      } catch (error) {
        console.error('Error loading classes:', error);
        toast.error('Failed to load classes');
      }
    };
    loadClasses();
  }, [filters.curriculum]);
  
  // Load subjects when class changes
  useEffect(() => {
    if (!filters.curriculum || !filters.classId) {
      setSubjects([]);
      return;
    }
    
    const loadSubjects = async () => {
      try {
        const data = await getSubjects(filters.curriculum, filters.classId);
        setSubjects(data);
        if (data.length > 0) {
          setFilters((f) => ({ ...f, subject: data[0].id }));
        } else {
          setFilters((f) => ({ ...f, subject: '' }));
        }
      } catch (error) {
        console.error('Error loading subjects:', error);
        toast.error('Failed to load subjects');
      }
    };
    loadSubjects();
  }, [filters.curriculum, filters.classId]);
  
  // Load chapters when all filters are set
  const loadChapters = useCallback(async (reset = true) => {
    /*
      Mid-cascade, hold what is on screen.

      Choosing a curriculum sets the class, which sets the subject, each through
      its own effect — so this runs two or three times before the filters are
      complete. Clearing the list on the incomplete passes made it flash empty
      and then repopulate on every filter change. The results are about to be
      replaced either way; showing nothing in the meantime helps nobody.
    */
    if (!filters.curriculum || !filters.classId || !filters.subject) return;

    setLoading(true);
    try {
      const result: GetChaptersResult = await getChapters({
        curriculumId: filters.curriculum,
        classId: filters.classId,
        subjectId: filters.subject,
        searchTerm: filters.search,
        lastDoc: reset ? undefined : (lastDoc as any),
      });
      
      /*
        No second read per chapter.

        getChapters already reads every document and keeps only the summary
        fields; asking for each one again was a full extra read per row on every
        filter change and every debounced keystroke, for data it had in hand.

        The list is also no longer filtered by language here. That filtering used
        to write `chapters` from `chapters`, so a chapter hidden by one language
        was gone from the array the next switch would have to find it in — it
        could never come back. The visible list is derived below instead.
      */
      const nextRaw = result.chapters
        .map((chapter) => ({ id: chapter.id, data: result.raw.get(chapter.id)! }))
        .filter((entry) => entry.data);

      if (reset) {
        setRawChapters(nextRaw);
      } else {
        setRawChapters((prev) => [...prev, ...nextRaw]);
      }
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading chapters:', error);
      toast.error('Failed to load chapters');
    } finally {
      setLoading(false);
    }
    // `selectedLanguage` is deliberately absent: language is a display filter,
    // applied to what has been fetched, and re-fetching on a language switch was
    // work for a result that would have been identical.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, lastDoc]);
  
  useEffect(() => {
    loadChapters(true);
  }, [filters.curriculum, filters.classId, filters.subject]);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.curriculum && filters.classId && filters.subject) {
        loadChapters(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);
  
  const handleOpenChapter = (
    chapter: Chapter,
    chapterGroup?: {
      chapterIds: string[];
      groupKey: string;
    }
  ) => {
    navigate(`/studio/content/${chapter.id}`, {
      state: {
        curriculumId: filters.curriculum,
        classId: filters.classId,
        subjectId: filters.subject,
        language: selectedLanguage,
        chapterGroupChapterIds: chapterGroup?.chapterIds,
        chapterGroupKey: chapterGroup?.groupKey,
      },
    });
  };
  
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadChapters(false);
    }
  };

  // Merge raw chapter data (with topics) for the table so topic rows and approval display correctly
  const tableChapters = useMemo(() => {
    return rawChapters
      .filter((rc) => chapterHasContentForLanguage(rc.data, selectedLanguage))
      .map((rc) => ({
        id: rc.id,
        chapter_number: rc.data.chapter_number,
        chapter_name: rc.data.chapter_name,
        current_version: 'v1',
        topic_count: rc.data.topics?.length ?? 0,
        updated_at: rc.data.updatedAt,
        curriculum_id: rc.data.curriculum,
        class_id: String(rc.data.class),
        subject_id: rc.data.subject,
        curriculum: rc.data.curriculum,
        class: rc.data.class,
        subject: rc.data.subject,
        topics: rc.data.topics ?? [],
        approved: rc.data.approved,
        approvedAt: rc.data.approvedAt,
        approvedBy: rc.data.approvedBy,
      })) as Chapter[];
  }, [rawChapters, selectedLanguage]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex">
        {/* Filters sidebar - no overlay, homogeneous with app sidebar */}
        <aside
          className={`min-h-screen bg-card border-r border-border p-5
            fixed sm:sticky top-0 left-0 z-30 transition-all duration-300 flex-shrink-0 flex flex-col
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
            ${isFilterCollapsed ? 'w-0 sm:p-0 sm:border-r-0 overflow-hidden opacity-0 sm:opacity-0' : 'w-72 sm:opacity-100'}`}
          style={isFilterCollapsed ? { minWidth: 0 } : { minWidth: '288px' }}
        >
          <div className="space-y-5">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-primary" />
                <span className="font-semibold text-sm text-foreground uppercase tracking-wider">Filters</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden sm:flex h-8 w-8 text-muted-foreground hover:bg-muted"
                  onClick={() => setIsFilterCollapsed(true)}
                  aria-label="Collapse filters"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="sm:hidden h-8 w-8 text-muted-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close filters"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {loadingFilters && (
              <div className="space-y-4 animate-pulse">
                <div className="space-y-2">
                  <div className="h-3 w-20 bg-muted rounded" />
                  <div className="h-10 bg-muted rounded-md" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-16 bg-muted rounded" />
                  <div className="h-10 bg-muted rounded-md" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-20 bg-muted rounded" />
                  <div className="h-10 bg-muted rounded-md" />
                </div>
              </div>
            )}

            {!loadingFilters && (
              <>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Curriculum</Label>
                  <Select
                    value={filters.curriculum}
                    onValueChange={(v) => setFilters((f) => ({ ...f, curriculum: v }))}
                    disabled={loadingFilters}
                  >
                    <SelectTrigger className="w-full h-10 border-input bg-background text-foreground">
                      <SelectValue placeholder="Select curriculum" />
                    </SelectTrigger>
                    <SelectContent>
                      {curriculums.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Class</Label>
                  <Select
                    value={filters.classId}
                    onValueChange={(v) => setFilters((f) => ({ ...f, classId: v }))}
                    disabled={!filters.curriculum || classes.length === 0}
                  >
                    <SelectTrigger className="w-full h-10 border-input bg-background text-foreground">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Subject</Label>
                  <Select
                    value={filters.subject}
                    onValueChange={(v) => setFilters((f) => ({ ...f, subject: v }))}
                    disabled={!filters.classId || subjects.length === 0}
                  >
                    <SelectTrigger className="w-full h-10 border-input bg-background text-foreground">
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t border-border my-6" />

                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Search chapters</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                      placeholder="Search by name..."
                      className="pl-10 border-input bg-background text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>

                <Card className="border border-border bg-muted/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <FolderOpen className="w-4 h-4 text-primary" />
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Results</span>
                    </div>
                    <p className="text-2xl font-semibold text-foreground">
                      {tableChapters.length}
                      <span className="text-sm font-normal text-muted-foreground ml-2">chapters</span>
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </aside>

        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-background/80 z-20 sm:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden
          />
        )}

        {/* Main content - in-flow title bar, no fixed header (theme-aware for dark mode) */}
        <main className={`flex-1 min-w-0 p-4 sm:p-6 bg-background text-foreground transition-all duration-300 w-full`}>
          {/* Page title and actions - in flow, does not overlay sidebar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3 min-w-0">
              {isFilterCollapsed && (
                <Button
                  variant="outline"
                  size="icon"
                  className="hidden sm:flex h-9 w-9 text-muted-foreground shrink-0 border-border bg-card"
                  onClick={() => setIsFilterCollapsed(false)}
                  aria-label="Expand filters"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              <div className="shrink-0 p-2 rounded-lg bg-primary/10 border border-border">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-foreground tracking-tight">Content Studio</h1>
                <p className="text-xs text-muted-foreground truncate">Curriculum Editor</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="sm:hidden gap-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Filter className="w-4 h-4" />
                Filters
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="gap-2"
                onClick={() => loadChapters(true)}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {loading && tableChapters.length === 0 && (
            <Card className="border border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <PrismFluxLoader
                  size={40}
                  speed={5}
                  textSize={12}
                  statuses={['Fetching', 'Loading', 'Syncing', 'Processing', 'Updating', 'Placing']}
                />
              </CardContent>
            </Card>
          )}

          {!loading && tableChapters.length === 0 && filters.subject && (
            <Card className="border border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="p-4 rounded-xl bg-muted/50 mb-4">
                  <FolderOpen className="w-12 h-12 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No chapters found</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  {filters.search
                    ? `No chapters match "${filters.search}". Try a different search term.`
                    : 'No chapters available for the selected curriculum, class, and subject.'}
                </p>
              </CardContent>
            </Card>
          )}

          {!filters.subject && !loading && (
            <Card className="border border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="p-4 rounded-xl bg-muted/50 mb-4">
                  <Filter className="w-12 h-12 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">Select filters</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Choose a curriculum, class, and subject from the sidebar to view chapters.
                </p>
              </CardContent>
            </Card>
          )}

          {tableChapters.length > 0 && (
            <>
              <ChapterTable
                chapters={tableChapters}
                onOpenChapter={handleOpenChapter}
                loading={loading}
                onApprovalChange={() => loadChapters(true)}
              />
              {hasMore && (
                <div className="flex justify-center mt-6">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default ContentLibrary;
